import React from 'react';
import { View, Text, ScrollView, ActivityIndicator } from 'react-native';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { StyleSheet, useUnistyles } from 'react-native-unistyles';
import Toast from 'react-native-toast-message';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { updateRecurringGift } from '@/data/giving';
import RecurringGiftList from '@/components/Giving/RecurringGiftList';

export default function RecurringGiftsScreen() {
    const queryClient = useQueryClient();
    const { theme: { colors } } = useUnistyles();
    const { data: loggedInUser } = Query.people.loggedInUser();
    const organizationID = loggedInUser?.organizationID;
    const personID = loggedInUser?.id;

    const { data: recurringGifts, isFetching } = Query.giving.recurringGifts({ organizationID, personID });
    const { data: funds } = Query.contributionFunds.all();

    const updateMutation = useMutation({
        mutationFn: ({ recurringGiftID, status }) => updateRecurringGift({ recurringGiftID, status }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.giving.recurringGifts(organizationID, personID) });
            Toast.show({ type: 'success', text2: 'Recurring gift updated' });
        },
        onError: (error) => {
            Toast.show({ type: 'error', text2: error?.message || 'Failed to update recurring gift' });
        },
    });

    const handleUpdate = (gift, status) => {
        updateMutation.mutate({ recurringGiftID: gift.id, status });
    };

    if (!loggedInUser || isFetching) {
        return (
            <View style={[styles.container, styles.center]} testID="recurring-gifts-loading">
                <ActivityIndicator color={colors?.giving} />
            </View>
        );
    }

    return (
        <ScrollView style={styles.container} contentContainerStyle={styles.content} testID="recurring-gifts-screen">
            <Text style={styles.title}>Recurring Gifts</Text>
            <RecurringGiftList
                testID="recurring-gifts-list"
                gifts={recurringGifts || []}
                funds={funds || []}
                onUpdate={handleUpdate}
            />
        </ScrollView>
    );
}

const styles = StyleSheet.create(({ colors, fonts, spacing, typography }) => ({
    container: {
        flex: 1,
        backgroundColor: 'transparent',
    },
    content: {
        padding: spacing.md,
        gap: spacing.md,
        _web: {
            maxWidth: 800,
            alignSelf: 'center',
            width: '100%',
        },
    },
    center: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
    },
    title: {
        fontSize: typography.h1,
        fontFamily: fonts.heading,
        color: colors.text,
        paddingVertical: spacing.sm,
    },
}));
